import { useTranslation } from 'react-i18next';
import {
  CONTACT_PREFERENCE_OPTIONS,
  COURIER_OPTIONS,
  PAYMENT_METHODS,
  RETURN_POLICY_OPTIONS,
} from './AdFormConstants';
import { CollapsibleSection } from './FormSection';

const SECTION_ICON = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="3" width="15" height="13" rx="2" ry="2" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const CHECK_ICON = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="2 7 5.5 10.5 12 4" />
  </svg>
);

export default function DeliverySection({ values, onChange, handleChange }) {
  const { t } = useTranslation();
  return (
    <CollapsibleSection
      icon={SECTION_ICON}
      title={t('createAd.deliveryWarrantyPolicies')}
      subtitle={t('createAd.optionalHelpsTrust')}
    >
      <div className="cf-field">
        <div className="cf-checkbox">
          <input
            id="ad-delivery-available"
            type="checkbox"
            checked={Boolean(values.delivery?.available)}
            onChange={(e) =>
              onChange('delivery', {
                ...(values.delivery || {}),
                available: e.target.checked,
              })
            }
          />
          <label htmlFor="ad-delivery-available">{t('createAd.deliveryAvailable')}</label>
        </div>
        {values.delivery?.available && (
          <div className="cf-nested-fields">
            <div className="cf-row cf-row--wrap">
              <div className="cf-field cf-field--third">
                <label>{t('createAd.deliveryType')}</label>
                <select
                  className="cf-input"
                  value={values.delivery?.type || 'pickup'}
                  onChange={(e) =>
                    onChange('delivery', {
                      ...values.delivery,
                      type: e.target.value,
                    })
                  }
                >
                  <option value="pickup">{t('createAd.pickupOnly')}</option>
                  <option value="delivery">{t('createAd.delivery')}</option>
                  <option value="both">{t('createAd.both')}</option>
                </select>
              </div>
              <div className="cf-field cf-field--third">
                <label>{t('createAd.shippingCost')}</label>
                <input
                  className="cf-input"
                  type="text"
                  inputMode="decimal"
                  value={
                    values.delivery?.shippingCost == null ||
                    values.delivery.shippingCost === ''
                      ? ''
                      : values.delivery.shippingCost
                  }
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Allow the user to type freely (including trailing dots or
                    // partial decimals like "12." or "" while editing). Only
                    // coerce to a number when it's a valid decimal.
                    const cleaned = raw.replace(/,/g, '.');
                    const parsed = cleaned === '' ? '' : Number(cleaned);
                    onChange('delivery', {
                      ...values.delivery,
                      shippingCost:
                        cleaned === '' || Number.isNaN(parsed) ? cleaned : parsed,
                    });
                  }}
                  placeholder="0"
                />
              </div>
              <div className="cf-field cf-field--third">
                <label>{t('createAd.estimatedDays')}</label>
                <input
                  className="cf-input"
                  type="text"
                  value={values.delivery?.estimatedDays || ''}
                  onChange={(e) =>
                    onChange('delivery', {
                      ...values.delivery,
                      estimatedDays: e.target.value,
                    })
                  }
                  placeholder={t('createAd.estimatedDaysPlaceholder')}
                />
              </div>
            </div>
            <div className="cf-field">
              <label>{t('createAd.courier')}</label>
              <select
                className="cf-input"
                value={
                  COURIER_OPTIONS.includes(values.delivery?.courier)
                    ? values.delivery.courier
                    : ''
                }
                onChange={(e) =>
                  onChange('delivery', {
                    ...values.delivery,
                    courier: e.target.value,
                  })
                }
              >
                <option value="">{t('createAd.selectCourier')}</option>
                {COURIER_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {t(`couriers.${c}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
      <div className="cf-divider" />
      <div className="cf-field">
        <div className="cf-checkbox">
          <input
            id="ad-warranty-available"
            type="checkbox"
            checked={Boolean(values.warranty?.available)}
            onChange={(e) =>
              onChange('warranty', {
                ...(values.warranty || {}),
                available: e.target.checked,
              })
            }
          />
          <label htmlFor="ad-warranty-available">{t('createAd.warrantyAvailable')}</label>
        </div>
        {values.warranty?.available && (
          <div className="cf-nested-fields">
            <div className="cf-row">
              <div className="cf-field cf-field--half">
                <label>{t('createAd.warrantyType')}</label>
                <input
                  className="cf-input"
                  type="text"
                  value={values.warranty?.type || ''}
                  onChange={(e) =>
                    onChange('warranty', {
                      ...values.warranty,
                      type: e.target.value,
                    })
                  }
                  placeholder={t('createAd.warrantyPlaceholder')}
                />
              </div>
              <div className="cf-field cf-field--half">
                <label>{t('createAd.warrantyDetails')}</label>
                <input
                  className="cf-input"
                  type="text"
                  value={values.warranty?.description || ''}
                  onChange={(e) =>
                    onChange('warranty', {
                      ...values.warranty,
                      description: e.target.value,
                    })
                  }
                  placeholder={t('createAd.warrantyDetailsPlaceholder')}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="cf-divider" />
      <div className="cf-field">
        <label htmlFor="ad-returnPolicy">{t('createAd.returnPolicy')}</label>
        <select
          id="ad-returnPolicy"
          className="cf-input"
          value={values.returnPolicy || 'no_returns'}
          onChange={handleChange('returnPolicy')}
        >
          {RETURN_POLICY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(`returnPolicies.${opt.key}`)}
            </option>
          ))}
        </select>
      </div>
      <div className="cf-divider" />
      <div className="cf-field">
        <label>{t('createAd.paymentMethodsAccepted')}</label>
        <div className="cf-chip-group">
          {PAYMENT_METHODS.map((pm) => {
            const checked =
              Array.isArray(values.paymentMethods) &&
              values.paymentMethods.includes(pm.value);
            return (
              <button
                key={pm.value}
                type="button"
                className={`cf-chip ${checked ? 'cf-chip--active' : ''}`}
                onClick={() => {
                  const current = values.paymentMethods || [];
                  const next = checked
                    ? current.filter((x) => x !== pm.value)
                    : [...current, pm.value];
                  onChange('paymentMethods', next);
                }}
              >
                {checked && CHECK_ICON}
                {t(`paymentMethods.${pm.key}`)}
              </button>
            );
          })}
        </div>
      </div>
      <div className="cf-divider" />
      <div className="cf-field">
        <label>{t('createAd.contactPreference')}</label>
        <div className="cf-chip-group">
          {CONTACT_PREFERENCE_OPTIONS.map((opt) => {
            const active = (values.contactPreference || 'both') === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`cf-chip ${active ? 'cf-chip--active' : ''}`}
                onClick={() => onChange('contactPreference', opt.value)}
              >
                {active && CHECK_ICON}
                {t(`contactPreferences.${opt.key}`)}
              </button>
            );
          })}
        </div>
      </div>
    </CollapsibleSection>
  );
}
